import type { SortOption } from '@/components/search/searchUtils';
import { addRecentSearch, sortProducts } from '@/components/search/searchUtils';
import { fetchProductsByCategory, searchProducts } from '@/lib/api';
import { useBranchStore } from '@/store/branchStore';
import { Product } from '@/types/product';
import { useCallback, useEffect, useState } from 'react';

interface UseProductSearchResult {
  products: Product[];
  pagination: { total: number; totalPages: number; page: number; limit: number } | null;
  isLoading: boolean;
  error: string | null;
  prescriptionCount: number;
  otcCount: number;
  setPage: (page: number) => void;
  page: number;
}

export function useProductSearch(
  query: string,
  prescriptionFilter: boolean | null,
  sortBy: SortOption,
  debounceMs: number = 300,
  limit: number = 12
): UseProductSearchResult {
  const [products, setProducts] = useState<Product[]>([]);
  const [pagination, setPagination] = useState<{ total: number; totalPages: number; page: number; limit: number } | null>(null);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prescriptionCount, setPrescriptionCount] = useState(0);
  const [otcCount, setOtcCount] = useState(0);
  const { branchId } = useBranchStore();

  const fetchProducts = useCallback(async () => {
    if (!branchId) {
      setProducts([]);
      setPagination(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      let result;

      if (prescriptionFilter === true) {
        result = await fetchProductsByCategory(branchId, 'POM', page, limit);
      } else if (prescriptionFilter === false) {
        result = await fetchProductsByCategory(branchId, 'OTC', page, limit);
      } else if (query.trim()) {
        result = await searchProducts(branchId, query.trim(), page, limit);
        addRecentSearch(query.trim());
      } else {
        result = { data: [], pagination: null };
      }

      const sorted = sortProducts(result.data, sortBy);
      setProducts(sorted);
      setPagination(result.pagination || null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch products');
      setProducts([]);
      setPagination(null);
    } finally {
      setIsLoading(false);
    }
  }, [branchId, query, prescriptionFilter, sortBy, page, limit]);

  const fetchCounts = useCallback(async () => {
    if (!branchId) return;

    try {
      // Fetch just 1 item to get the total count
      const [pomResult, otcResult] = await Promise.all([
        fetchProductsByCategory(branchId, 'POM', 1, 1),
        fetchProductsByCategory(branchId, 'OTC', 1, 1),
      ]);

      setPrescriptionCount(pomResult.pagination?.total || 0);
      setOtcCount(otcResult.pagination?.total || 0);
    } catch {
      setPrescriptionCount(0);
      setOtcCount(0);
    }
  }, [branchId]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  // Reset page to 1 when filters or query change
  useEffect(() => {
    setPage(1);
  }, [query, prescriptionFilter, sortBy]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchProducts();
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [fetchProducts, debounceMs]);

  return {
    products,
    pagination,
    isLoading,
    error,
    prescriptionCount,
    otcCount,
    setPage,
    page,
  };
}
